/**
 * Voice Tracker–style split for bot call summaries (paragraph + ## Agent/Customer/Outcome).
 * Prefer final Vobiz notes; skip duplicating ## Summary into the notes pane.
 */

export function isVoiceAwaiting(text) {
  return /awaiting vobiz recording/i.test(String(text || ''));
}

export function isVoiceTranscriptFallback(text) {
  return /transcript fallback|summary status:\s*fallback/i.test(String(text || ''));
}

export function isVoiceFinal(text) {
  const t = String(text || '');
  if (isVoiceAwaiting(t) || isVoiceTranscriptFallback(t)) return false;
  return /summary status:\s*final/i.test(t);
}

export function hasVoiceStructuredNotes(text) {
  return /##\s+(Agent|Customer|Outcome|Ops|Driver|Summary)\b/i.test(String(text || ''));
}

/**
 * Keep only ## Agent / Customer / Outcome notes (same as Voice Tracker UI).
 * Drops ## Summary, ## Ops, and leading prose (Summary card / system meta).
 */
function normalizeNotesMarkdown(raw) {
  let text = String(raw || '').trim();
  if (!text) return { notesMarkdown: '', leadingProse: '' };

  text = text
    .replace(/##\s*Summary\s*\n[\s\S]*?(?=\n##\s|\s*$)/gi, '')
    .replace(/##\s*Summary\s+.+?(?=\s##\s+(?:Agent|Customer|Outcome|Ops|Driver)\b|$)/gi, '')
    // Ops is pipeline meta — not shown in Voice Tracker or ops bot Call History
    .replace(/##\s*Ops\s*\n[\s\S]*?(?=\n##\s|\s*$)/gi, '')
    .replace(/##\s*Ops\s+.+?(?=\s##\s+(?:Agent|Customer|Outcome|Driver|Summary)\b|$)/gi, '')
    .replace(/\s*(##\s+(?:Agent|Customer|Outcome|Driver|Summary)\b)/gi, '\n$1')
    .trim();

  // Prose before first conversation heading duplicates the Summary card
  const firstHeading = text.search(/##\s+(Agent|Customer|Outcome|Driver)\b/i);
  let leadingProse = '';
  if (firstHeading > 0) {
    leadingProse = text.slice(0, firstHeading).trim();
    text = text.slice(firstHeading).trim();
  } else if (firstHeading < 0 && !/##\s+(Agent|Customer|Outcome|Driver)\b/i.test(text)) {
    leadingProse = text;
    text = '';
  }

  text = text
    .split('\n')
    .filter((ln) => !/awaiting vobiz recording/i.test(ln))
    .join('\n')
    .trim();

  return { notesMarkdown: text, leadingProse };
}

/** Build display fields from a marketing voice history/detail row (same as Tracker). */
export function buildTrackerStyleSummary(row) {
  const conversation = String(row?.conversation_summary || '').trim();
  const tracking = String(row?.tracking_summary || '').trim();
  const display = String(row?.call_summary || '').trim();
  const kind = String(row?.recording_summary_kind || '').toLowerCase();

  const notesSource = (
    tracking && !isVoiceAwaiting(tracking)
      ? tracking
      : hasVoiceStructuredNotes(display)
        ? display
        : hasVoiceStructuredNotes(conversation)
          ? conversation
          : tracking
  );

  let summaryText = '';
  if (conversation && !hasVoiceStructuredNotes(conversation)) {
    summaryText = conversation;
  } else if (display && !hasVoiceStructuredNotes(display) && !/^\[Voice Call /i.test(display)) {
    summaryText = display;
  } else if (display) {
    summaryText = display.replace(/^\[Voice Call [^\]]+\]\s*/i, '').trim();
    if (hasVoiceStructuredNotes(summaryText)) {
      const m = summaryText.match(/##\s*Summary\s*\n([\s\S]*?)(?=\n##\s|\s*$)/i);
      summaryText = m ? m[1].replace(/\s+/g, ' ').trim() : '';
    }
  }
  if (!summaryText && notesSource) {
    const m = notesSource.match(/##\s*Summary\s*\n([\s\S]*?)(?=\n##\s|\s*$)/i);
    if (m) summaryText = m[1].replace(/\s+/g, ' ').trim();
  }

  const { notesMarkdown, leadingProse } = normalizeNotesMarkdown(notesSource);
  if (!summaryText && leadingProse) {
    summaryText = leadingProse;
  }

  const awaiting = Boolean(row?.recording_awaiting_vobiz) || isVoiceAwaiting(tracking);
  const fallback = Boolean(row?.recording_transcript_fallback)
    || kind === 'fallback'
    || isVoiceTranscriptFallback(tracking);
  const finalKind = kind === 'vobiz' || isVoiceFinal(tracking);

  return {
    summaryText: summaryText.replace(/\s+/g, ' ').trim(),
    notesMarkdown,
    awaiting,
    fallback,
    isFinal: finalKind && !awaiting && !fallback,
  };
}

/** Split a CRM `[Voice Call …] body` entry into Tracker-style parts (fallback path). */
export function buildTrackerStyleFromCrmEntry(entry) {
  const body = String(entry?.body || entry?.text || '').trim();
  return buildTrackerStyleSummary({
    conversation_summary: hasVoiceStructuredNotes(body) ? '' : body,
    tracking_summary: hasVoiceStructuredNotes(body) ? body : '',
    call_summary: body,
  });
}
